import React, { useState, useEffect, useRef } from "react";
import "./NewImageDisplay.css";
import "cropperjs/dist/cropper.css";
import ChatOverlay from "../../components/ChatOverlay";
import TransparentHeader from "../../components/TransparentHeader";
import Overlay from "../../components/Overlay";
import PlayerCard from "../../components/PlayerCard";
import ObjectCard from "../../components/ObjectCard";


const NewImageDisplay = ({
  photo,
  image,
  modelConfig,
  handleClose,
  logoUrl,
  titleHeader,
  config,
  text,
  imagePrompt,
  onImageUpload,
  usePortal = false,

}) => {


  const commonFieldKeys = ["age", "weight", "height", "profile_pic", "Name", "jersey_no", "jersey_color"];
  const structuredPlayerDetails = {
    common_fields: {},
    sport_specific: {}
  };

  // Add object detection enabled state
  const [objectDetectionEnabled, setObjectDetectionEnabled] = useState(true); // Enable by default

  const [faceData, setFaceData] = useState([]);
  const imageRef = useRef(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [selectedFaceData, setSelectedFaceData] = useState(null);
  const [dotPositions, setDotPositions] = useState([]);
  const [firstExternalImageId, setFirstExternalImageId] = useState(null);

  // Add object detection state
  const [objectData, setObjectData] = useState([]);
  const [boxPositions, setBoxPositions] = useState([]);
  const [boundingBoxPositions, setBoundingBoxPositions] = useState([]);
  const [selectedObjectData, setSelectedObjectData] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_HOST_URL?.trim() || "https://devapi.fiduciaai.in";


  // helper function to convert the jpeg data to png 
  const convertImageToPngBlob = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'Anonymous';

      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            }
            else {
              reject(new Error("Canvas to Blob conversion failed"));
            }
          },
          "image/png"
        );
      };

      image.onerror = (error) => {
        reject(error);
      };

      image.src = imageUrl;

    });
  };

  // Add object detection API function
  const fetchObjectData = async () => {
    try {
      console.debug("Starting object detection conversion to png..");
      const pngBlob = await convertImageToPngBlob(image);
      console.debug("Successfully converted to PNG blob");

      const imageFile = new File([pngBlob], "image.png", { type: "image/png" });

      const formData = new FormData();
      formData.append("threshold", "80.0");
      formData.append("max_objects", "1");
      formData.append("file", imageFile);

      const apiResponse = await fetch(
        BASE_URL + "/rag/api/v2/yolo/img/search",
        {
          method: "POST",
          headers: {
            accept: "application/json",
          },
          body: formData,
        }
      );

      if (!apiResponse.ok) {
        throw new Error(`API Error: ${apiResponse.statusText}`);
      }

      const data = await apiResponse.json();

      // Handle nested response structure with results.detections
      let detections = [];
      if (data.results && data.results.detections) {
        detections = data.results.detections;
      } else if (Array.isArray(data)) {
        // Fallback to direct array if needed
        detections = data;
      } else {
        console.warn("Unexpected response structure:", data);
        detections = [];
      }

      // const processedObjects = detections.map(object => {
      //   if (object && object.match && object.match.details) {
      //     const structuredDetails = {
      //       common_fields: {},
      //       detection_specific: {}
      //     };

      //     // Handle new details structure (simple string instead of JSON)
      //     let parsedData = {};
      //     const detailsString = object.match.details;

      //     // Try to parse as JSON first (for backward compatibility)
      //     try {
      //       const cleanedString = detailsString.replace(/\\"/g, '"');
      //       parsedData = JSON.parse(cleanedString);
      //     } catch (error) {
      //       // If not JSON, treat as simple class name
      //       parsedData = {
      //         class_name: detailsString || "object",
      //         confidence: object.match.similarity || 0.0
      //       };
      //     }

      //     // Structure the data similar to face recognition
      //     structuredDetails.common_fields = {
      //       name: parsedData.class_name || "object",
      //       confidence: object.match.similarity || 0.0,
      //       class_id: parsedData.class_id !== undefined ? parsedData.class_id : null  // Extract class_id from parsed data
      //     };

      //     // Add bounding box information
      //     if (object.bounding_box) {
      //       structuredDetails.common_fields.bbox = [
      //         object.bounding_box.left,
      //         object.bounding_box.top,
      //         object.bounding_box.width,
      //         object.bounding_box.height
      //       ];
      //     }

      //     // Add any additional parsed data to detection_specific
      //     for (const key in parsedData) {
      //       if (!["class_name", "confidence"].includes(key)) {
      //         structuredDetails.detection_specific[key] = parsedData[key];
      //       }
      //     }

      //     return {
      //       ...object,
      //       objectDetails: structuredDetails,
      //       objectId: object.object_index
      //     };
      //   }
      //   return object;
      // });

      const processedObjects = detections.map(object => {
        if (object && object.match && object.match.details) {
          const structuredDetails = {
            common_fields: {},
            detection_specific: {}
          };

          // Normalize confidence: old API returned 0–100, new API returns 0–1
          const similarity = object.match?.similarity ?? 0;
          const normalizedConfidence = similarity > 1 ? similarity / 100 : similarity;

          // Parse stringified details safely (supports both escaped and plain JSON)
          let parsedData = {};
          const detailsString = object.match.details || "";
          try {
            const cleanedString = detailsString.replace(/\\"/g, '"');
            parsedData = JSON.parse(cleanedString);
          } catch {
            // Fallback: treat as plain label
            parsedData = { class_name: detailsString || "object" };
          }

          // Prefer new field names but keep backward compatibility
          const nameFromDetails = parsedData.class_name || parsedData.Name;
          const imageFromDetails = parsedData.dress_image || parsedData.image || parsedData.image_url;

          structuredDetails.common_fields = {
            name: nameFromDetails || object.object_index || "object",
            confidence: normalizedConfidence,
            class_id: parsedData.class_id ?? null,
            preview_image_url: imageFromDetails || null
          };

          // Add bounding box
          if (object.bounding_box) {
            structuredDetails.common_fields.bbox = [
              object.bounding_box.left,
              object.bounding_box.top,
              object.bounding_box.width,
              object.bounding_box.height
            ];
          }

          // Copy all other details into detection_specific, excluding ones we already mapped
          const excludeKeys = new Set([
            "class_name",
            "class_id",
            "confidence",
            "Name",
            "dress_image",
            "image",
            "image_url"
          ]);

          for (const key in parsedData) {
            if (!excludeKeys.has(key)) {
              structuredDetails.detection_specific[key] = parsedData[key];
            }
          }

          return {
            ...object,
            objectDetails: structuredDetails,
            objectId: object.object_index
          };
        }
        return object;
      });

      setObjectData(processedObjects);
    } catch (error) {
      console.error("Failed to fetch object data: ", error);
    }
  };


  useEffect(() => {

    if (!image) {
      return;
    }

    const fetchFaceData = async () => {
      try {
        console.debug("Starting conversion to png..");
        const pngBlob = await convertImageToPngBlob(image);
        console.debug("Successfully converted to PNG blob");

        const imageFile = new File([pngBlob], "image.png", { type: "image/png" });

        const formData = new FormData();
        formData.append("collection_id", "faces_collection");
        formData.append("threshold", "90.0");
        formData.append("max_faces", "1");
        formData.append("file", imageFile);


        const apiResponse = await fetch(
          BASE_URL + "/rag/api/v2/rekognition/img/search",
          {
            method: "POST",
            headers: {
              accept: "application/json",
            },
            body: formData,
          }
        );

        console.log("API Response: ", apiResponse);

        if (!apiResponse.ok) {
          throw new Error(`API Error: ${apiResponse.statusText}`);
        }

        const data = await apiResponse.json();

        const processedFaces = data.map(face => {
          if (face.matches && face.matches.length > 0 && face.matches[0].description) {
            const rawString = face.matches[0].description;
            const cleanedString = rawString.replace(/\\"/g, '"');
            const parsedData = JSON.parse(cleanedString);

            const structuredDetails = {
              common_fields: {},
              sport_specific: {}
            };


            for (const key in parsedData) {
              if (commonFieldKeys.includes(key)) {
                structuredDetails.common_fields[key] = parsedData[key];
              } else {
                structuredDetails.sport_specific[key] = parsedData[key];
              }
            }
            return {
              ...face,
              playerDetails: structuredDetails,
              externalImageId: face.matches[0].external_image_id
            };
          }
          return face;
        });

        setFaceData(processedFaces);

        if (data.length > 0 && data[0].matches && data[0].matches.length > 0) {
          setFirstExternalImageId(data[0].matches[0].external_image_id);
        } else {
          setFirstExternalImageId(null);
        }
      } catch (error) {
        console.error("Failed to fetch face data: ", error);
      }
    };

    // Run face recognition always, and object detection if enabled
    if (objectDetectionEnabled) {
      // Run both APIs in parallel
      Promise.all([fetchFaceData(), fetchObjectData()]);
    } else {
      // Run only face recognition
      fetchFaceData();
    }

    setIsImageLoaded(false);
  }, [image, objectDetectionEnabled]);


  useEffect(() => {
    const updateDotPositions = () => {
      const imageEl = imageRef.current;
      if (!imageEl || !faceData || faceData.length === 0) {
        setDotPositions([]);
        return;
      }

      const imageOffsetX = imageEl.offsetLeft;
      const imageOffsetY = imageEl.offsetTop;

      const renderedWidth = imageEl.offsetWidth;
      const renderedHeight = imageEl.offsetHeight;
      const originalWidth = imageEl.naturalWidth;
      const originalHeight = imageEl.naturalHeight;

      if (originalWidth === 0 || originalHeight === 0 || renderedWidth === 0) {
        return;
      }

      const scaleX = renderedWidth / originalWidth;
      const scaleY = renderedHeight / originalHeight;

      const newPositions = faceData.map(face => {
        const midX_original = face.bounding_box.left + face.bounding_box.width / 2;
        const midY_original = face.bounding_box.top + face.bounding_box.height / 2;


        const finalLeft = imageOffsetX + (midX_original * scaleX);
        const finalTop = imageOffsetY + (midY_original * scaleY);

        return {
          key: face.face_index,
          faceData: face,
          style: {
            left: `${finalLeft}px`,
            top: `${finalTop}px`,
            cursor: 'pointer'
          }
        };
      });

      setDotPositions(newPositions);
    };

    // Add object dot position calculation function (matching face recognition pattern)
    const updateObjectDotPositions = () => {
      const imageEl = imageRef.current;
      if (!imageEl || !objectData || objectData.length === 0) {
        setBoxPositions([]);
        setBoundingBoxPositions([]);
        return;
      }

      const imageOffsetX = imageEl.offsetLeft;
      const imageOffsetY = imageEl.offsetTop;

      const renderedWidth = imageEl.offsetWidth;
      const renderedHeight = imageEl.offsetHeight;
      const originalWidth = imageEl.naturalWidth;
      const originalHeight = imageEl.naturalHeight;

      if (originalWidth === 0 || originalHeight === 0 || renderedWidth === 0) {
        return;
      }

      const scaleX = renderedWidth / originalWidth;
      const scaleY = renderedHeight / originalHeight;

      const newObjectPositions = objectData.map(object => {
        const bbox = object.bounding_box;

        // Calculate center point of bounding box
        const midX_original = bbox.left + bbox.width / 2;
        const midY_original = bbox.top + bbox.height / 2;

        const finalLeft = imageOffsetX + (midX_original * scaleX);
        const finalTop = imageOffsetY + (midY_original * scaleY);

        return {
          key: object.objectId,
          objectData: object,
          style: {
            left: `${finalLeft}px`,
            top: `${finalTop}px`,
            cursor: 'pointer'
          }
        };
      });

      // Calculate bounding box positions
      const newBoundingBoxPositions = objectData.map((object, index) => {
        const bbox = object.bounding_box;
        const scaledLeft = imageOffsetX + (bbox.left * scaleX);
        const scaledTop = imageOffsetY + (bbox.top * scaleY);
        const scaledWidth = bbox.width * scaleX;
        const scaledHeight = bbox.height * scaleY;

        return {
          key: `object-box-${index}`,
          style: {
            position: 'absolute',
            left: `${scaledLeft}px`,
            top: `${scaledTop}px`,
            width: `${scaledWidth}px`,
            height: `${scaledHeight}px`,
            border: '2px solid #ff6b35',
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 1
          }
        };
      });

      setBoxPositions(newObjectPositions);
      setBoundingBoxPositions(newBoundingBoxPositions);
    };


    const updateAllPositions = () => {
      // Always update face positions
      updateDotPositions();

      // Update object positions if object detection is enabled
      if (objectDetectionEnabled) {
        updateObjectDotPositions();
      }
    };

    if (isImageLoaded) {
      updateAllPositions();
    }
    window.addEventListener('resize', updateAllPositions);
    return () => window.removeEventListener('resize', updateAllPositions);
  }, [faceData, objectData, isImageLoaded, objectDetectionEnabled]);



  const handleDotClick = (face) => {

    if (face && face.playerDetails) {
      setSelectedFaceData(face);
      setSelectedObjectData(null); // Clear object selection
      setIsOverlayOpen(true);
    } else {
      console.debug("No player details found for this specific face.");
    }
  }

  // Add object click handler (matching face recognition pattern)
  const handleObjectDotClick = (object) => {
    if (object && object.objectDetails) {
      setSelectedObjectData(object);
      setSelectedFaceData(null); // Clear face selection
      setIsOverlayOpen(true);
    } else {
      console.debug("No object details found for this specific object.");
    }
  }

  const handleCloseOverlay = () => {
    setIsOverlayOpen(false);
    setSelectedFaceData(null);
  }






  return (
    <div className="image-display">
      <TransparentHeader
        showLeftIcon={true}
        logoUrl={logoUrl}
        leftIconCallbackMethod={handleClose}
        titleHeader={titleHeader}
      />

      {/* Object Detection Toggle */}
      <div className="detection-toggle">
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={objectDetectionEnabled}
            onChange={(e) => setObjectDetectionEnabled(e.target.checked)}
          />
          <span className="toggle-slider"></span>
          <span className="toggle-label">Object Detection</span>
        </label>

      </div>

      <div className="photo-container">
        <img src={image} alt="Captured or Uploaded" ref={imageRef} onLoad={() => setIsImageLoaded(true)} />

        {/* Object Detection Bounding Boxes */}
        {objectDetectionEnabled && !isOverlayOpen && boundingBoxPositions.map((box) => (
          <div
            key={box.key}
            style={box.style}
          />
        ))}

        {/* Face Recognition Dots (always shown) */}
        {dotPositions.map((dot) => (
          <div
            key={dot.key}
            className="dot-clickable-area"
            style={dot.style}
            onClick={() => handleDotClick(dot.faceData)}
          >
            <div className="blinking-dot" />
          </div>
        ))}

        {/* Object Detection Dots (shown when enabled) */}
        {objectDetectionEnabled && !isOverlayOpen && boxPositions.map((dot) => (
          <div
            key={dot.key}
            className="dot-clickable-area"
            style={dot.style}
            onClick={() => handleObjectDotClick(dot.objectData)}
          >
            <div className="blinking-dot" />
          </div>
        ))}
      </div>

      <ChatOverlay
        usePortal={usePortal}
        timestamp={new Date().getTime()}
        modelConfig={modelConfig}
        image={image}
        config={config}
        text={text}
        imagePrompt={imagePrompt}
        onImageUpload={onImageUpload}
        hasBottomBar={false}
        externalImageId={firstExternalImageId}
      />

      <Overlay
        isOpen={isOverlayOpen}
        onClose={handleCloseOverlay}
      // Pass the components and the selected data down
      >
        {selectedFaceData && selectedFaceData.playerDetails && (
          <PlayerCard
            player={selectedFaceData.playerDetails}
            onClose={handleCloseOverlay}
            teamName="India"
          />
        )}
        {selectedObjectData && selectedObjectData.objectDetails && (
          <ObjectCard
            object={selectedObjectData.objectDetails}
            onClose={handleCloseOverlay}
          />
        )}
      </Overlay>
    </div>
  );
};

export default NewImageDisplay;


